
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ActivityModal } from "./ActivityModal";
import { ActivityEditModal } from "./ActivityEditModal";
import { Clock, User, Calendar } from "lucide-react";
import { format, parseISO } from "date-fns";
import type { Activity } from "@/hooks/use-activities";

const activityTypeColors = {
  "Meeting": "bg-blue-100 text-blue-800 border-blue-200",
  "Phone Call": "bg-green-100 text-green-800 border-green-200",
  "Email Communication": "bg-purple-100 text-purple-800 border-purple-200",
  "Proposal Presentation": "bg-orange-100 text-orange-800 border-orange-200",
  "Site Visit": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Follow-up": "bg-gray-100 text-gray-800 border-gray-200",
  "Other": "bg-pink-100 text-pink-800 border-pink-200",
};

interface ActivitiesTabProps {
  activities: Activity[];
  isLoading?: boolean;
  contactId?: string;
  staffId?: string;
  partnerId?: string;
  title?: string;
}

export const ActivitiesTab = ({ 
  activities, 
  isLoading, 
  contactId, 
  staffId, 
  partnerId,
  title = "Activities"
}: ActivitiesTabProps) => {
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-sm text-slate-500">Loading activities...</div>
      </div>
    );
  }

  const handleActivityClick = (activity: Activity) => {
    setSelectedActivity(activity);
    setEditModalOpen(true);
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-blue-600" />
          {title} ({activities.length})
        </h3>
        <ActivityModal 
          defaultContactId={contactId}
          defaultStaffId={staffId}
          defaultPartnerId={partnerId}
        />
      </div>

      {activities.length === 0 ? (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-blue-600" />
            </div>
            <h4 className="font-semibold text-slate-800 mb-2">No activities yet</h4>
            <p className="text-sm text-slate-600 mb-4">
              Start tracking interactions and meetings
            </p>
            <ActivityModal 
              defaultContactId={contactId}
              defaultStaffId={staffId}
              defaultPartnerId={partnerId}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {activities.map((activity) => {
            const activityDate = parseISO(activity.activity_date);
            const performer = activity.staff || activity.partner;
            
            return (
              <Card 
                key={activity.id} 
                className="hover:shadow-md transition-all duration-200 cursor-pointer border-slate-200 hover:border-blue-300 group"
                onClick={() => handleActivityClick(activity)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                        {activity.title}
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-slate-600 mt-2">
                        <div className="flex items-center gap-1.5">
                          <Clock className="h-4 w-4 text-slate-400" />
                          {format(activityDate, "MMM dd, yyyy 'at' HH:mm")}
                        </div>
                        {performer && !staffId && !partnerId && (
                          <div className="flex items-center gap-1.5">
                            <User className="h-4 w-4 text-slate-400" />
                            {performer.first_name} {performer.last_name}
                          </div>
                        )}
                        {activity.contact && !contactId && (
                          <div className="flex items-center gap-1.5">
                            <User className="h-4 w-4 text-slate-400" />
                            {activity.contact.first_name} {activity.contact.last_name}
                          </div>
                        )}
                      </div>
                    </div>
                    <Badge className={`${activityTypeColors[activity.activity_type]} text-xs font-medium`}>
                      {activity.activity_type}
                    </Badge>
                  </div>
                  
                  {activity.notes && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-lg border">
                      <p className="text-sm text-slate-700 leading-relaxed">
                        {activity.notes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {selectedActivity && (
        <ActivityEditModal
          activity={selectedActivity}
          open={editModalOpen}
          onOpenChange={(open) => {
            setEditModalOpen(open);
            if (!open) setSelectedActivity(null);
          }}
        />
      )}
    </div>
  );
};
